import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Public Components
import { LandingPage } from "@/components/public/LandingPage";
import { WizardPage } from "@/components/public/WizardPage";
import { OfflinePage } from "@/components/public/OfflinePage";

// Auth Components
import { LoginPage } from "@/components/auth/LoginPage";

// Admin Components
import { AdminLayout } from "@/components/admin/AdminLayout";
import { StandorteUebersicht } from "@/components/admin/StandorteUebersicht";
import { InstrumenteUndGruppen } from "@/components/admin/InstrumenteUndGruppen";
import { LehrpersonenList } from "@/components/admin/LehrpersonenList";
import { AngeboteList } from "@/components/admin/AngeboteList";
import { AngeboteUebersicht } from "@/components/admin/AngeboteUebersicht";
import { ExportTools } from "@/components/admin/ExportTools";
import StatistikPage from "@/components/admin/StatistikPage";

// Einstellungen - separate pages
import { EinstellungenAllgemein } from "@/components/admin/EinstellungenAllgemein";
import { EinstellungenTexte } from "@/components/admin/EinstellungenTexte";
import { EinstellungenBenutzer } from "@/components/admin/EinstellungenBenutzer";
import { EinstellungenRollen } from "@/components/admin/EinstellungenRollen";
import { EinstellungenSystem } from "@/components/admin/EinstellungenSystem";

// Lehrer Portal
import { LehrerPortal } from "@/components/lehrer/LehrerPortal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wrapper to check offline status for public routes
function PublicRouteWrapper({ children }) {
    const [siteOffline, setSiteOffline] = useState(false);
    const [offlineMessage, setOfflineMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    
    useEffect(() => {
        const checkOfflineStatus = async () => {
            try {
                const response = await fetch(`${API_URL}/api/public/settings`);
                if (response.ok) {
                    const settings = await response.json();
                    // Use maintenance_mode (the source of truth in backend)
                    setSiteOffline(settings.maintenance_mode || false);
                    setOfflineMessage(settings.maintenance_message || 'Derzeit nicht erreichbar');
                }
            } catch (err) {
                console.error('Failed to check offline status:', err);
            } finally {
                setLoading(false);
            }
        };
        checkOfflineStatus();
    }, []);
    
    // Allow access to admin and login even when offline
    const isAdminOrLogin = location.pathname.startsWith('/admin') || 
                           location.pathname.startsWith('/login') ||
                           location.pathname.startsWith('/lehrer');
    
    if (loading) {
        return <div className="min-h-screen bg-gray-50"></div>;
    }
    
    if (siteOffline && !isAdminOrLogin) {
        return <OfflinePage message={offlineMessage} />;
    }
    
    return children;
}

function App() {
    return (
        <>
            <BrowserRouter>
                <PublicRouteWrapper>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/finden" element={<WizardPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/lehrer" element={<LehrerPortal />} />
                        
                        {/* Admin Routes */}
                        <Route path="/admin" element={
                            <AdminLayout>
                                <AngeboteUebersicht />
                            </AdminLayout>
                        } />
                        <Route path="/admin/uebersicht" element={
                            <Navigate to="/admin" replace />
                        } />
                        <Route path="/admin/standorte" element={
                            <AdminLayout>
                                <StandorteUebersicht />
                            </AdminLayout>
                        } />
                        <Route path="/admin/instrumente" element={
                            <AdminLayout>
                                <InstrumenteUndGruppen />
                            </AdminLayout>
                        } />
                        <Route path="/admin/lehrpersonen" element={
                            <AdminLayout>
                                <LehrpersonenList />
                            </AdminLayout>
                        } />
                        <Route path="/admin/angebote" element={
                            <AdminLayout>
                                <AngeboteList />
                            </AdminLayout>
                        } />
                        <Route path="/admin/einstellungen" element={
                            <AdminLayout>
                                <EinstellungenAllgemein />
                            </AdminLayout>
                        } />
                        <Route path="/admin/einstellungen/texte" element={
                            <AdminLayout>
                                <EinstellungenTexte />
                            </AdminLayout>
                        } />
                        <Route path="/admin/einstellungen/benutzer" element={
                            <AdminLayout>
                                <EinstellungenBenutzer />
                            </AdminLayout>
                        } />
                        <Route path="/admin/einstellungen/rollen" element={
                            <AdminLayout>
                                <EinstellungenRollen />
                            </AdminLayout>
                        } />
                        <Route path="/admin/einstellungen/system" element={
                            <AdminLayout>
                                <EinstellungenSystem />
                            </AdminLayout>
                        } />
                        <Route path="/admin/export" element={
                            <AdminLayout>
                                <ExportTools />
                            </AdminLayout>
                        } />
                        <Route path="/admin/statistik" element={
                            <AdminLayout>
                                <StatistikPage />
                            </AdminLayout>
                        } />
                        
                        {/* Legacy redirect */}
                        <Route path="/admin/instrumentengruppen" element={<Navigate to="/admin/instrumente" replace />} />

                        {/* Fallback */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </PublicRouteWrapper>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
        </>
    );
}

export default App;
